import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

// KD-3581 §3/§4 — the additional services of one scope, in three states.

export enum AdditionalServicesBandScopeEnum {
  DAY = 'day',
  GLOBAL = 'global',
}

export enum AdditionalServicesBandStateEnum {
  AVAILABLE = 'available',
  SELECTED = 'selected',
  UNAVAILABLE = 'unavailable',
}
//
// Geometry, colours and type sizes come from the mockup attached to the ticket,
// measured on its rendered DOM (see .workflow/KD-3581/maquette-reference.md).
// The values that have no equivalent on the Tailwind scale are written as
// arbitrary ones rather than snapped to the nearest step: the ticket asks for a
// rendering identical to the mockup, and the nearest step is visibly off on the
// orange (#EA6A17 vs orange-600 #EA580C) and on the half-pixel type sizes.
//
// The day scope uses orange for its title, icon and action button; the global
// scope uses purple instead, so a global (whole-experience) service reads as
// visually distinct from a per-day one at a glance. The tint the selected state
// paints the band with follows that same accent, otherwise the container would
// frame purple contents in orange. Amounts stay orange in both — see
// totalVariants below.
//
// The chips sit OUTSIDE the trigger, as siblings inside the wrapper: they carry
// their own remove button, and a button inside a button is invalid markup.
const bandVariants = cva('overflow-hidden border border-solid', {
  variants: {
    scope: {
      [AdditionalServicesBandScopeEnum.DAY]: 'rounded-[11px]',
      [AdditionalServicesBandScopeEnum.GLOBAL]: 'rounded-xl',
    },
    state: { available: '', selected: '', unavailable: '' },
  },
  compoundVariants: [
    {
      scope: AdditionalServicesBandScopeEnum.DAY,
      state: 'available',
      class: 'border-[#EFF2F7] bg-[#FBFCFE]',
    },
    {
      scope: AdditionalServicesBandScopeEnum.DAY,
      state: AdditionalServicesBandStateEnum.SELECTED,
      class: 'border-[#FBDCC3] bg-[#FFF6EF]',
    },
    {
      scope: AdditionalServicesBandScopeEnum.GLOBAL,
      state: AdditionalServicesBandStateEnum.AVAILABLE,
      class: 'border-[#EFF2F7] bg-white',
    },
    {
      scope: AdditionalServicesBandScopeEnum.GLOBAL,
      state: AdditionalServicesBandStateEnum.SELECTED,
      class: 'border-purple-200 bg-purple-50',
    },
  ],
  defaultVariants: {
    scope: AdditionalServicesBandScopeEnum.GLOBAL,
    state: AdditionalServicesBandStateEnum.AVAILABLE,
  },
});

// Not clickable, and never hidden: an absence of offer is information (§4).
// The mockup only shows it on the day scope; the global one is unspecified
// there (AC03) and traced on the same style rather than invented.
const emptyVariants = cva(
  'rounded-[11px] border border-dashed border-[#E4E9F0] px-3 py-[11px] text-[12.5px] text-[#A0AAB8]',
);

// Wraps rather than crushes, like the frame header: side by side with a
// version the panel is half as wide, and the amount and the action are
// incompressible. Left to shrink against them the wording collapsed to one
// letter per line, so it keeps a floor of its own and the money group drops to
// a second line when what is left is not enough for both.
const triggerVariants = cva(
  'flex w-full flex-wrap items-center border-0 bg-transparent text-left transition-colors hover:bg-black/[0.02] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  {
    variants: {
      scope: {
        [AdditionalServicesBandScopeEnum.DAY]: 'gap-[11px] px-3 py-[11px]',
        [AdditionalServicesBandScopeEnum.GLOBAL]: 'gap-3 px-3.5 py-3',
      },
    },
    defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
  },
);

const iconVariants = cva(
  'flex shrink-0 items-center justify-center rounded-lg',
  {
    variants: {
      scope: {
        [AdditionalServicesBandScopeEnum.DAY]:
          'h-[30px] w-[30px] text-[13px] bg-[#FFF0E4] text-[#EA6A17]',
        [AdditionalServicesBandScopeEnum.GLOBAL]:
          'h-8 w-8 rounded-[9px] text-[14px] bg-purple-100 text-purple-600',
      },
    },
    defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
  },
);

const titleVariants = cva('block font-semibold', {
  variants: {
    scope: {
      [AdditionalServicesBandScopeEnum.DAY]: 'text-[13.5px] text-[#EA6A17]',
      [AdditionalServicesBandScopeEnum.GLOBAL]: 'text-[14px] text-purple-600',
    },
  },
  defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
});

// Amounts stay orange whatever the scope: §9 reserves that orange for money.
const totalVariants = cva(
  'shrink-0 whitespace-nowrap font-bold text-orange-500',
  {
    variants: {
      scope: {
        [AdditionalServicesBandScopeEnum.DAY]: 'text-[13.5px]',
        [AdditionalServicesBandScopeEnum.GLOBAL]: 'text-[14px]',
      },
    },
    defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
  },
);

const actionVariants = cva(
  'flex shrink-0 items-center rounded-lg font-semibold text-white',
  {
    variants: {
      scope: {
        [AdditionalServicesBandScopeEnum.DAY]:
          'h-[30px] px-[13px] text-[12.5px] bg-[#EA6A17]',
        [AdditionalServicesBandScopeEnum.GLOBAL]:
          'h-8 px-3.5 text-[13px] bg-purple-600',
      },
    },
    defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
  },
);

// The total and the action button move as one block, so a wrap never leaves
// the amount alone on its line. The gaps mirror the trigger's own.
const moneyGroupVariants = cva('ml-auto flex shrink-0 items-center', {
  variants: {
    scope: {
      [AdditionalServicesBandScopeEnum.DAY]: 'gap-[11px]',
      [AdditionalServicesBandScopeEnum.GLOBAL]: 'gap-3',
    },
  },
  defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
});

const chipsVariants = cva('flex flex-wrap gap-2', {
  variants: {
    scope: {
      [AdditionalServicesBandScopeEnum.DAY]: 'px-3 pb-[11px]',
      [AdditionalServicesBandScopeEnum.GLOBAL]: 'px-3.5 pb-3',
    },
  },
  defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
});

// The mockup draws these two with placeholder glyphs (▤ and ◎) the way it draws
// every other icon of the panel; the panel itself runs on Font Awesome, so the
// glyphs are transcribed rather than copied.
const scopeIcons = {
  [AdditionalServicesBandScopeEnum.DAY]: 'fa-light fa-list-ul',
  [AdditionalServicesBandScopeEnum.GLOBAL]: 'fa-light fa-circle-dot',
} as const;

type AdditionalServicesBandProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'onClick'
> &
  Omit<VariantProps<typeof bandVariants>, 'scope' | 'state'> & {
    scope?: AdditionalServicesBandScopeEnum;
    state?: AdditionalServicesBandStateEnum;
    /** Band title, e.g. "Global additional services". */
    title?: React.ReactNode;
    /** Count and scope in one sentence: "3 available · apply to the whole experience". */
    subtitle?: React.ReactNode;
    /** Cumulative amount of the selected services. Ignored unless selected — an
     *  available band shows no price at all (§4). */
    totalLabel?: React.ReactNode;
    /** KD-3744 — per-traveller share of that amount, e.g. "€40 / pax". Shown
     *  under the total, and only with it. */
    perPaxLabel?: React.ReactNode;
    /** Add or Edit, depending on the state. Omitted in read-only views, and the
     *  whole band then stops being clickable with it. */
    actionLabel?: React.ReactNode;
    onAction?: React.MouseEventHandler<HTMLButtonElement>;
    /** Full sentence shown in the unavailable state. */
    emptyLabel?: React.ReactNode;
    /** The AdditionalServiceChip list, rendered under the trigger when selected. */
    children?: React.ReactNode;
  };

const AdditionalServicesBand = React.forwardRef<
  HTMLDivElement,
  AdditionalServicesBandProps
>(
  (
    {
      scope = AdditionalServicesBandScopeEnum.GLOBAL,
      state = AdditionalServicesBandStateEnum.AVAILABLE,
      title,
      subtitle,
      totalLabel,
      perPaxLabel,
      actionLabel,
      onAction,
      emptyLabel,
      children,
      ...props
    },
    ref,
  ) => {
    if (state === AdditionalServicesBandStateEnum.UNAVAILABLE) {
      return (
        <div ref={ref} className={emptyVariants()} {...props}>
          {emptyLabel}
        </div>
      );
    }

    const isSelected = state === AdditionalServicesBandStateEnum.SELECTED;

    return (
      <div ref={ref} className={bandVariants({ scope, state })} {...props}>
        <button
          type="button"
          onClick={onAction}
          disabled={!onAction}
          className={triggerVariants({ scope })}
        >
          <span className={iconVariants({ scope })} aria-hidden="true">
            <i className={scopeIcons[scope]} />
          </span>

          <span className="min-w-[7.5rem] flex-1">
            <span className={titleVariants({ scope })}>{title}</span>

            {subtitle && (
              <span className="mt-px block text-[12.5px] text-slate-500">
                {subtitle}
              </span>
            )}
          </span>

          {((isSelected && totalLabel) || actionLabel) && (
            <span className={moneyGroupVariants({ scope })}>
              {isSelected && totalLabel && (
                <span className="flex flex-col items-end">
                  <span className={totalVariants({ scope })}>{totalLabel}</span>

                  {perPaxLabel && (
                    <span className="whitespace-nowrap text-[11.5px] font-normal text-slate-500">
                      {perPaxLabel}
                    </span>
                  )}
                </span>
              )}

              {actionLabel && (
                <span className={actionVariants({ scope })}>{actionLabel}</span>
              )}
            </span>
          )}
        </button>

        {isSelected && children && (
          <div className={chipsVariants({ scope })}>{children}</div>
        )}
      </div>
    );
  },
);

AdditionalServicesBand.displayName = 'AdditionalServicesBand';

export default AdditionalServicesBand;
